import type { Contact, ContactAttributeValue } from '../types/contacts.types';

export const toContactStringValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => toContactStringValue(item))
      .filter((item): item is string => Boolean(item));
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['Name', 'name', 'Title', 'title', 'Value', 'value', 'Label', 'label']) {
      const nested = toContactStringValue(record[key]);
      if (nested) return nested;
    }
  }

  return undefined;
};

/**
 * Looks up a single `Attributes` entry by `PropertyKey`. The exact keys
 * below match the seeded default custom fields — see
 * scripts/seed-custom-field-definitions.ts.
 */
const getCustomAttributeValue = (
  contact: Contact | undefined,
  propertyKey: string
): ContactAttributeValue | undefined =>
  contact?.Attributes?.find((entry) => entry?.PropertyKey === propertyKey) ?? undefined;

export const getContactDisplayName = (contact: Contact | undefined): string => {
  if (!contact) return '';

  const fullName = [contact.FirstName, contact.LastName]
    .map((part) => toContactStringValue(part))
    .filter(Boolean)
    .join(' ');

  if (fullName) return fullName;

  if (contact.Email) {
    const localPart = contact.Email.split('@')[0] ?? contact.Email;
    return localPart
      .split(/[._-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return contact.ItemId;
};

export const getContactInitials = (contact: Contact | undefined): string => {
  const name = getContactDisplayName(contact);
  const parts = name.split(/\s+/).filter(Boolean);
  // Array.from iterates by code point so emoji / surrogate pairs are never split.
  const firstChar = (value: string) => Array.from(value)[0] ?? '';
  if (parts.length >= 2) {
    return `${firstChar(parts[0])}${firstChar(parts[1])}`.toUpperCase();
  }
  return Array.from(name).slice(0, 2).join('').toUpperCase();
};

export const getContactCity = (contact: Contact | undefined): string | undefined =>
  toContactStringValue(getCustomAttributeValue(contact, 'userCity')?.StringValue);

/**
 * The old `AdditionalAttributes.UserLoveProfileAge` field stored a single
 * per-contact age number. Its seeded replacement is the `ageRange` custom
 * field (DataType: NumberRange) — used for range-based *filtering*, but each
 * contact's own age is still stored as a single `NumberValue` under that
 * same PropertyKey (the numberRange operator only affects how the value is
 * queried, not how it's stored). See CUSTOM_ATTRIBUTE_VALUE_COLUMN in
 * filter-query-builder.ts, which also maps 'number'-valueType fields (which
 * NumberRange maps to) to NumberValue.
 */
export const getContactAge = (contact: Contact | undefined): number | undefined => {
  const raw = getCustomAttributeValue(contact, 'ageRange')?.NumberValue;
  if (raw === undefined || raw === null) return undefined;
  return Number.isFinite(raw) ? raw : undefined;
};

export const getContactUserType = (contact: Contact | undefined): string | undefined =>
  toContactStringValue(getCustomAttributeValue(contact, 'userType')?.StringValue);

export const getContactIndustry = (contact: Contact | undefined): string | undefined =>
  toContactStringValue(getCustomAttributeValue(contact, 'userIndustry')?.StringValue);

export const getContactPostalCode = (contact: Contact | undefined): number | undefined => {
  const raw = getCustomAttributeValue(contact, 'userPostalCode')?.NumberValue;
  return raw === null ? undefined : (raw ?? undefined);
};

export const getContactSubscriptionName = (contact: Contact | undefined): string | undefined =>
  toContactStringValue(getCustomAttributeValue(contact, 'userActiveSubscriptionName')?.StringValue);

export const getContactRegistrationDate = (contact: Contact | undefined): string | undefined => {
  const raw = getCustomAttributeValue(contact, 'registrationDate')?.DateValue;
  return raw === null ? undefined : (raw ?? undefined);
};

export const isContactActiveSubscription = (contact: Contact | undefined): boolean =>
  Boolean(getCustomAttributeValue(contact, 'isUserActiveSubscription')?.BoolValue);

export const isContactProfileCompleted = (contact: Contact | undefined): boolean =>
  Boolean(getCustomAttributeValue(contact, 'isProfileCompleted')?.BoolValue);

const isPlaceholderValue = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return normalized === 'sample text' || normalized === 'sample';
};

export const isContactBlacklisted = (contact: Contact | undefined): boolean => {
  const blacklistedBy = contact?.BlacklistedBy;
  if (blacklistedBy === null || blacklistedBy === undefined) return false;
  if (typeof blacklistedBy === 'boolean') return blacklistedBy;

  if (Array.isArray(blacklistedBy)) {
    return blacklistedBy.some((entry) => {
      const value = toContactStringValue(entry);
      return Boolean(value && !isPlaceholderValue(value));
    });
  }

  const value = toContactStringValue(blacklistedBy);
  if (!value || isPlaceholderValue(value)) return false;

  return true;
};

export const getContactBlacklistedByIds = (contact: Contact | undefined): string[] => {
  const blacklistedBy = contact?.BlacklistedBy;
  if (!blacklistedBy) return [];

  if (Array.isArray(blacklistedBy)) {
    return blacklistedBy
      .map((entry) => toContactStringValue(entry))
      .filter((value): value is string => Boolean(value && !isPlaceholderValue(value)));
  }

  const value = toContactStringValue(blacklistedBy);
  if (!value || isPlaceholderValue(value)) return [];
  return [value];
};

export const getContactStatusLabel = (contact: Contact | undefined): string => {
  if (!contact) return 'Inactive';
  if (isContactBlacklisted(contact)) return 'Blocked';
  if (isContactActiveSubscription(contact)) return 'Active';
  if (contact.IsNewsLetterSubscribed || contact.IsMarketingCampaignSubscribed) {
    return 'Subscribed';
  }
  return 'Inactive';
};

// getSuppressionLabel / getSuppressionClassName were removed along with
// Contact.SubscriptionStatus (the ESP-fed bounce/complaint/unsubscribe
// field). Suppression display returns once the replacement system-owned
// suppression signal is designed — tracked separately.

export const getContactLists = (contact: Contact | undefined): string[] => {
  const membershipNames =
    contact?.Lists?.map((list) => list.Name?.trim()).filter((name): name is string =>
      Boolean(name)
    ) ?? [];
  if (membershipNames.length > 0) return membershipNames;
  return contact?.Tags?.filter(Boolean) ?? [];
};
